'use client'

import { AnalyticsPageNames, AnalyticsPageProps, trackPage } from '@/app/utils/analytics'
import React from 'react'

export type AnalyticsPage = {
  name: AnalyticsPageNames
  props?: AnalyticsPageProps
}

export interface AnalyticsContextValues {
  setPage: (page: AnalyticsPage) => void
}

const AnalyticsContext = React.createContext<AnalyticsContextValues | undefined>(undefined)

const AnalyticsProvider = ({ children }: { children: React.ReactNode | React.ReactNode[] }): React.ReactElement => {
  const [currentPage, setCurrentPage] = React.useState<{
    name: AnalyticsPageNames
    props?: AnalyticsPageProps
  } | null>(null)

  // track end on window close
  React.useEffect(() => {
    if (!currentPage) {
      return
    }
    trackPage(currentPage.name)
  }, [currentPage])

  // track start and end with next navigation
  const setPage = React.useCallback((page: AnalyticsPage) => {
    setCurrentPage(page)
  }, [])

  return (
    <AnalyticsContext.Provider
      value={{
        setPage
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  )
}

/**
 * Custom hook for accessing analytics tracking context.
 *
 * This hook is specifically intended for use by the `AnalyticsPageTracker` component,
 * which handles memoization to ensure that page tracking events are only recorded
 * when necessary. This prevents erroneous or duplicate tracking events that could occur
 * from re-renders or other unintended interactions.
 *
 * Throws an error if used outside of a `AnalyticsProvider` context, ensuring that the
 * hook is used in a properly structured environment.
 *
 * @returns {AnalyticsContextValues} The analytics context necessary for tracking page views.
 */
function useAnalyticsPageTracker(): AnalyticsContextValues {
  const context = React.useContext(AnalyticsContext)
  if (context === undefined) {
    throw new Error('useAnalyticsPageTracker must be used within an AnalyticsProvider')
  }
  return context
}

// For backward compatibility
export type MixpanelPage = AnalyticsPage
export type MixpanelContextValues = AnalyticsContextValues
export const MixpanelProvider = AnalyticsProvider
export const useMixpanelPageTracker = useAnalyticsPageTracker

export { AnalyticsProvider, useAnalyticsPageTracker }
